<template>
  <div>
    <table style="width: 100%; ">
      <thead>
        <tr>
          <th>#</th>
          <th>{{ $t("task.name") }}</th>
          <th>{{ $t("task.description") }}</th>
          <th>{{ $t("task.assignee") }}</th>
          <th>{{ $t("task.status.text") }}</th>
          <!-- TKP-165 md.4 (karar 2026-10-01): öncelik, kategori, son tarih sütunları -->
          <th>{{ $t("task.priority.text") }}</th>
          <th>{{ $t("task.category") }}</th>
          <!-- TKP-239 g.5: görev tipi etkinse sütun; değilse liste değişmez -->
          <th v-if="typeEnabled" data-test="head-type">{{ $t("task.type") }}</th>
          <th>{{ $t("task.duedate") }}</th>
          <!-- TKP-176 md.49/50: kaynak sütunu yerine oluşturma tarihi (başlıktan sıralanır) -->
          <th class="hand" data-test="head-created-at" @click="toggleCreatedAtSort"><i class="fas fa-sort mr-1" :class="createdAtSortIcon"></i>{{ $t("common.created-at") }}</th>
          <th>{{ $t("organization.text") }}</th>
        </tr>
        <tr>
          <th><input :placeholder="$t('task.no-in-short')" class="form-control" v-model="noFilter" type="text" /></th>
          <th><input :placeholder="$t('task.name')" class="form-control" v-model="nameFilter" type="text" /></th>
          <th><input :placeholder="$t('task.description')" class="form-control" v-model="descriptionFilter" type="text" /></th>
          <th>
            <!-- TKP-165 md.4: her sütunda süzgeç — atanan ve organizasyon süzgeçleri her sekme/kapsamda -->
            <filter-select
              id="accountFilter"
              ref="accountFilter"
              :placeholder="$t('common.name')"
              :multiple="false"
              @filter="accountSearch"
              :selected="account"
              @changed="accountSelect"
            />
          </th>
          <th>
            <select v-model="statusFilter" class="form-control">
              <option :value="null">{{ $t("common.clear-selection") }}</option>
              <!-- TKP-119: organizasyonun durum kayıtları (özel durumlar dahil) -->
              <option v-for="s in statusFilterOptions" :key="s.key" :value="s.key">{{ s.label }}</option>
            </select>
          </th>
          <th>
            <select v-model="priorityFilter" class="form-control" data-test="priority-filter">
              <option :value="null">{{ $t("common.clear-selection") }}</option>
              <option v-for="p in priorityFilterOptions" :key="p.value" :value="p.value">{{ p.label }}</option>
            </select>
          </th>
          <th>
            <filter-select id="categoryFilter" ref="categoryFilter" :placeholder="$t('task.category')" :multiple="false" @filter="categorySearch" :selected="categoryFilter" @changed="categorySelect" />
          </th>
          <th v-if="typeEnabled">
            <!-- TKP-240 b.2: Görev Tipi süzgeci -->
            <filter-select id="typeFilter" ref="typeFilter" :placeholder="$t('task.type')" :multiple="false" @filter="typeSearch" :selected="typeFilter" @changed="typeSelect" data-test="type-filter" />
          </th>
          <th>
            <date-picker v-model="duedateFilter" range :placeholder="$t('task.duedate')" :lang="currentLanguage" @change="duedateSelect" data-test="duedate-filter" />
          </th>
          <th>
            <!-- TKP-181 md.50: oluşturulma tarihi aralığı -->
            <date-picker v-if="$listeners.createdAtFilter" v-model="createdAtFilter" range :placeholder="$t('common.created-at')" :lang="currentLanguage" @change="createdAtSelect" data-test="created-at-filter" />
          </th>
          <th>
            <!-- TKP-250 g.8: organizasyon sayfasında başka organizasyonun görevi listelenmez — süzgeç yalnız kullanıcı panelinde -->
            <filter-select
              v-if="scope === 'ACCOUNT'"
              id="organizationSelect"
              ref="organizationSelect"
              :placeholder="$t('organization.name')"
              :multiple="false"
              @filter="organizationSearch"
              :selected="organization"
              @changed="organizationSelect"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="task in tasks" :key="'task-list-' + task._id" @click="openDetail(task)" class="hand">
          <td class="gray-text">#{{ task.no }}</td>

          <td style="min-width:300px;">
            <div class="d-flex" style="gap: 1rem;">
              {{ task.name | translate | summary(50) }}
            </div>
          </td>

          <td class="gray-text">{{ task.description | translate | summary(150) }}</td>
          <td>
            <div class="d-flex align-items-center" style="gap: 0.62rem;">
              <task-avatar :account="task.assignee" />
              <span v-if="task.assignee">{{ task.assignee.name }}</span>
              <span v-else class="userName">{{ $t("task.unassigned") }}</span>
            </div>
          </td>
          <td style="min-width: 205px;">
            <div class="d-flex align-items-center">
              <task-status :status="task.status" />
            </div>
          </td>
          <td data-test="cell-priority"><task-priority :priority="task.priority" size="13" /></td>
          <td data-test="cell-category">{{ task.category && task.category.name ? $options.filters.translate(task.category.name) : "—" }}</td>
          <td v-if="typeEnabled" data-test="cell-type">{{ task.type && task.type.name ? $options.filters.translate(task.type.name) : "—" }}</td>
          <td data-test="cell-duedate"><small>{{ task.duedate ? $options.filters.date(task.duedate) : "—" }}</small></td>
          <td data-test="cell-created-at"><small>{{ task.createdAt ? $options.filters.date(task.createdAt) : "—" }}</small></td>
          <td style="min-width: 205px;">
            <div class="d-flex align-items-center">
              <!-- <letter-avatar :name="task.organization.name" :showName="true" size="xxs" :image="task.organization.logo" variant="secondary" /> -->
              {{ task.organization.name }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <content-loading v-if="isMyTasksLoading" />
    <content-loading v-if="isOrganizationTasksLoading" />
  </div>
</template>

<script>
import { resolvePriorities, resolveStatuses } from "@/modules/task/task-settings"; // TKP-119
import { nextSort } from "@/modules/task/task-filters"; // TKP-176 md.50
import OrganizationMemberSelect from "@cc/organization-member-select";
import DatePicker from "vue2-datepicker";
export default {
  props: ["name", "no", "description", "account", "status", "organization", "scope", "activeTab", "archived", "priority", "category", "type", "duedate", "createdAtSort", "createdAt"],
  components: { OrganizationMemberSelect, DatePicker },
  data() {
    return {
      nameFilter: this.name,
      noFilter: this.no,
      descriptionFilter: this.description,
      accountFilter: this.account,
      statusFilter: this.status,
      organizationFilter: this.organization,
      // TKP-165 md.4
      priorityFilter: this.priority ?? null,
      categoryFilter: this.category ?? null,
      typeFilter: this.type ?? null, // TKP-240 b.2
      duedateFilter: this.duedate ?? [],
      createdAtFilter: this.createdAt ?? [], // TKP-181 md.50
      createdAtSort: this.createdAtSort ?? null, // TKP-176 md.50: null | "asc" | "desc"
    };
  },
  watch: {
    nameFilter() {
      this.debounce(() => this.$emit("nameFilter", this.nameFilter));
    },
    noFilter() {
      this.debounce(() => this.$emit("noFilter", this.noFilter));
    },
    descriptionFilter() {
      this.debounce(() => this.$emit("descriptionFilter", this.descriptionFilter));
    },
    statusFilter() {
      this.$emit("statusFilter", this.statusFilter);
    },
    priorityFilter() {
      this.$emit("priorityFilter", this.priorityFilter);
    },
  },
  mounted() {},
  computed: {
    typeEnabled() {
      return this.$store.getters["taskSettings/typeEnabled"]; // TKP-239 g.5
    },
    createdAtSortIcon() {
      return !this.createdAtSort ? "fa-sort" : this.createdAtSort === "asc" ? "fa-sort-alpha-down" : "fa-sort-alpha-up";
    },
    statusFilterOptions() {
      const language = this.$store.getters["user/account"]?.language ?? "tr";
      return resolveStatuses(this.$store.getters["taskSettings/statuses"], language, (k) => this.$t(k));
    },
    priorityFilterOptions() {
      const language = this.$store.getters["user/account"]?.language ?? "tr";
      return resolvePriorities(this.$store.getters["taskSettings/priorities"], language, (k) => this.$t(k));
    },
    isOrganizationTasksLoading() {
      return this.$store.getters["task/isOrganizationTasksLoading"];
    },
    isMyTasksLoading() {
      return this.$store.getters["task/isMyTasksLoading"];
    },
    myTasksPagination() {
      return this.$store.getters["task/myTasksPagination"];
    },
    organizationTasksPagination() {
      return this.$store.getters["task/organizationTasksPagination"];
    },
    tasks() {
      if (this.activeTab == "account") {
        return this.myTasksPagination?.docs ?? [];
      } else {
        return this.organizationTasksPagination?.docs ?? [];
      }
    },
  },
  methods: {
    openDetail(task) {
      this.$store.commit("task/showTaskDetailModal", true);
      this.$store.dispatch("task/fetchDetail", task._id);
    },

    async accountSearch(value, callback) {
      const response = await this.$store.dispatch("organizationMember/paginateWith", {
        pagination: { query: { accessable: { value: true, isObject: true } } },
        organizationId: this.currentOrganization?._id,
      });
      callback(response.data?.docs ?? []);
    },

    accountSelect(acc) {
      if (!acc) {
        this.$emit("accountFilter", acc);
      } else {
        this.accountFilter = { _id: acc.accountId, name: acc.name };
        if (acc?._id != this.account?._id) {
          this.$emit("accountFilter", { ...this.accountFilter });
        }
      }
    },
    onStatusSelect(status) {
      if (!status) {
        this.status = null;
        this.$emit("statusFilter", null);
        return;
      }
      this.status = status;

      this.$emit("statusFilter", this.status);
    },
    async organizationSearch(value, callback) {
      const response = await this.$store.dispatch(`organization/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },

    // TKP-165 md.4: kategori (kütüphane; TKP-168 ile organizasyon kayıtlarına geçer), son tarih ve kaynak birim süzgeçleri
    /** TKP-168: organizasyonun kategorileri (Görevler › Ayarlar), kütüphane değil. */
    async categorySearch(value, callback) {
      try {
        callback(await this.$store.dispatch("taskSettings/searchCategories", value));
      } catch (error) {
        callback([]);
      }
    },
    /** TKP-240 b.2: organizasyonun görev tipleri (Görevler › Ayarlar › Görev Tipi). */
    async typeSearch(value, callback) {
      try {
        callback(await this.$store.dispatch("taskSettings/searchTypes", value));
      } catch (error) {
        callback([]);
      }
    },
    typeSelect(type) {
      this.typeFilter = type || null;
      this.$emit("typeFilter", this.typeFilter);
    },
    categorySelect(category) {
      this.categoryFilter = category || null;
      this.$emit("categoryFilter", this.categoryFilter);
    },
    duedateSelect(range) {
      this.duedateFilter = range;
      this.$emit("duedateFilter", range);
    },
    createdAtSelect(range) {
      this.createdAtFilter = range;
      this.$emit("createdAtFilter", range);
    },
    toggleCreatedAtSort() {
      this.createdAtSort = nextSort(this.createdAtSort);
      this.$emit("createdAtSort", this.createdAtSort);
    },
    organizationSelect(org) {
      this.organizationFilter = org;
      if (org?._id != this.organization?._id) {
        this.$emit("organizationFilter", { ...this.organizationFilter });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./tasks";
</style>

<style>
.multiselect--active {
  z-index: 9999999999 !important;
  position: static !important;
}
.multiselect__content-wrapper {
  max-width: 300px !important;
}
</style>
